/* ------------------------------------------------------------------
   vu-warm.js (2026-09-06)

   Fetches the images the browser will otherwise NEVER fetch.

   loading="lazy" defers on viewport intersection. An image the browser
   cannot see has no intersection, so "not needed yet" becomes "never":
     - a card hidden with display:none (the homepage formula pills)
     - slides 2..N of a slick carousel, translated out and clipped by
       .slick-list { overflow: hidden }
     - the off-canvas cart and menu drawers
     - cards scrolled out of a horizontal rail
   Nothing requests those until a click reveals them, and every one of
   those containers has a grey or dark fill behind it -- so the click
   paints the fill and the picture arrives a beat later. That is the
   reported grey box.

   Flipping loading to "eager" restarts the load for an image that has
   not begun one. This runs on window LOAD, after everything that matters
   to first paint is done, so it costs the initial render nothing and
   makes every one of those clicks instant.

   Scoped deliberately for the HIDDEN pass below: only containers that
   HIDE or CLIP their contents are listed there, because those are the
   ones the browser would otherwise never fetch at all.

   2026-09-21: a SECOND, blanket pass was added after it.

   Until now every image in the theme was loading="eager" (the 7 Sep pass,
   after "all images should load on arrival"). That worked, but it put the
   whole below-the-fold set on the critical path: measured on the live home
   page, 816 KB of images sitting 3.5 to 8.3 screens down were fetched
   BEFORE DOMContentLoaded, against just 257 KB actually in the viewport.
   On a fast line that is invisible; on 4G it is the LCP image queueing
   behind pictures nobody has scrolled to yet.

   So the below-the-fold sections are lazy again, and this pass flips ALL
   of them to eager on window LOAD. Same end state -- everything is in
   cache without the visitor scrolling -- but sequenced AFTER first paint
   instead of competing with it. They are warmed at fetchpriority="low" so
   they cannot contend with anything still in flight.
   ------------------------------------------------------------------ */
(function () {
  'use strict';

  var HIDDEN_CONTAINERS = [
    '.slick-list',            /* every slick carousel: gallery, thumbs, badges,
                                 athlete cards, system breakdown, related */
    '[data-vurange-card]',    /* homepage formula pills -- display:none cards */
    '.vurange__rail',         /* ...and the ones scrolled off that rail */
    '.vsci__rail',            /* homepage science rail */
    '.visys__grid',           /* homepage "Introducing" rail */
    '.exsys__drawer',         /* homepage formula accordion, clipped to 0fr */
    '.menu-drawer',           /* burger drawer, parked off-canvas */
    '.cart-drawer'            /* cart drawer, parked off-canvas */
  ];

  function warm() {
    var seen = 0;

    HIDDEN_CONTAINERS.forEach(function (sel) {
      var roots = document.querySelectorAll(sel);
      for (var r = 0; r < roots.length; r++) {
        var imgs = roots[r].querySelectorAll('img[loading="lazy"]');
        for (var i = 0; i < imgs.length; i++) {
          if (imgs[i].complete) { continue; }
          imgs[i].loading = 'eager';
          seen++;
        }
      }
    });

    /* Blanket pass: everything still lazy after the scoped pass above.
       This is the below-the-fold set. Warmed in DOM order (top of page
       first) and at low priority, so the visitor scrolling immediately
       still gets the nearest picture first. */
    var rest = document.querySelectorAll('img[loading="lazy"]');
    for (var j = 0; j < rest.length; j++) {
      if (rest[j].complete) { continue; }
      try { rest[j].fetchPriority = 'low'; } catch (e) {}
      rest[j].loading = 'eager';
      seen++;
    }

    /* The flavour swatches on a shop tile swap the packshot by writing
       data-img into img.src (sections/main-collection-product-grid.liquid).
       Nothing on the page ever requests those URLs, so the first tap on a
       swatch always blanks the tile. Pull them into cache instead. */
    var radios = document.querySelectorAll('.da-card-variants[data-img]');
    for (var v = 0; v < radios.length; v++) {
      var url = radios[v].getAttribute('data-img');
      if (!url) { continue; }
      var pre = new Image();
      pre.decoding = 'async';
      pre.src = url;
      seen++;
    }

    return seen;
  }

  if (document.readyState === 'complete') {
    warm();
  } else {
    window.addEventListener('load', warm, { once: true });
  }

  /* slick can still be cloning slides when load fires (it initialises from
     main.js, which is deferred behind a 149 KB bundle). One late pass picks
     up anything that appeared after. Idempotent -- warmed images are no
     longer [loading="lazy"], so they are not matched twice. */
  setTimeout(warm, 2500);
})();
